import React from 'react'
import { FormApi } from 'final-form'
import { Field } from 'react-final-form'

import {
  Button,
  CheckboxField,
  InputField,
  SelectField,
  TextareaField,
} from '@mc/design-system'
import { Trans, useTranslation } from '@mc/i18n'
import { PostToHubspotV3Props } from '../utils/postToHubspot'

type FormBodyProps = {
  form: FormApi<PostToHubspotV3Props, Partial<PostToHubspotV3Props>>
  errors: boolean
  loading: boolean
  submitted: boolean
  onSubmit: () => void
  handleValueChange: (
    value: number,
    form: FormApi<PostToHubspotV3Props, Partial<PostToHubspotV3Props>>,
    option: keyof PostToHubspotV3Props,
  ) => void
  defaultEmail: string
  valid: boolean
}

export const FormBody = ({
  onSubmit,
  handleValueChange,
  form,
  defaultEmail,
  errors,
  submitted,
  loading,
  valid,
}: FormBodyProps) => {
  const { t } = useTranslation('@mc/enterprise-contact-form')

  return (
    <form
      className='d-flex flex-column mc-w-100'
      style={{ gap: 'var(--mc-scale-6)' }}
      onSubmit={onSubmit}
    >
      <div className='d-flex flex-wrap' style={{ gap: 'var(--mc-scale-6)' }}>
        <div className='flex-1' style={{ minWidth: '300px' }}>
          <Field
            component={InputField}
            id='firstName'
            name='firstName'
            label={t('groupForm.seats.form.inputFirstName.label')}
            type='text'
            placeholder={t('groupForm.seats.form.inputFirstName.label')}
            required
            autoComplete='given-name'
          />
        </div>
        <div className='flex-1' style={{ minWidth: '300px' }}>
          <Field
            component={InputField}
            id='lastName'
            name='lastName'
            label={t('groupForm.seats.form.inputLastName.label')}
            type='text'
            placeholder={t('groupForm.seats.form.inputLastName.label')}
            required
            autoComplete='family-name'
          />
        </div>
      </div>
      <Field
        component={InputField}
        id='email'
        name='email'
        label={t('groupForm.seats.form.inputEmail.label')}
        type='email'
        placeholder={t('groupForm.v2.emailPlaceholder')}
        initialValue={defaultEmail}
        required
        autoComplete='email'
      />
      <Field
        component={InputField}
        id='jobTitle'
        name='jobTitle'
        label={t('groupForm.seats.form.inputTitle.label')}
        type='text'
        required
        placeholder={t('groupForm.v2.jobTitle')}
      />
      <div className='d-flex flex-wrap' style={{ gap: 'var(--mc-scale-6)' }}>
        <div className='flex-1' style={{ minWidth: '300px' }}>
          <Field
            component={InputField}
            id='organizationName'
            name='organizationName'
            label={t('groupForm.seats.form.inputOrg.label')}
            type='text'
            placeholder={t('groupForm.v2.companyName')}
            required
            autoComplete='organization'
          />
        </div>
        <div className='flex-1' style={{ minWidth: '300px' }}>
          <Field
            component={SelectField}
            name='employeeSize'
            label={t('groupForm.seats.form.employeeSize.label')}
            options={[
              t('groupForm.seats.form.employeeSize.options.sub200'),
              t('groupForm.seats.form.employeeSize.options.201to500'),
              t('groupForm.seats.form.employeeSize.options.501to1000'),
              t('groupForm.seats.form.employeeSize.options.1001to5000'),
              t('groupForm.seats.form.employeeSize.options.5001to10000'),
              t('groupForm.seats.form.employeeSize.options.10001plus'),
            ].map((value) => ({
              value,
              label: value,
            }))}
            placeholder={t('groupForm.seats.form.employeeSize.placeholder')}
            onChange={(value: number) =>
              handleValueChange(value, form, 'employeeSize')
            }
          />
        </div>
      </div>
      <Field
        component={SelectField}
        name='useCase'
        label={t('groupForm.seats.form.useCase.label')}
        options={[
          t('groupForm.seats.form.useCase.options.enterpriseWideLearning'),
          t('groupForm.seats.form.useCase.options.leadershipDevelopment'),
          t('groupForm.seats.form.useCase.options.salesTrainingContent'),
          t('groupForm.seats.form.useCase.options.wellnessContent'),
          t('groupForm.seats.form.useCase.options.refundsRequest'),
          t('groupForm.seats.form.useCase.options.partnerships'),
          t('groupForm.seats.form.useCase.options.other'),
        ].map((value) => ({
          value,
          label: value,
        }))}
        placeholder={t('groupForm.seats.form.useCase.placeholder')}
        onChange={(value: number) => handleValueChange(value, form, 'useCase')}
        required
      />
      <Field
        component={SelectField}
        name='whenAreYouLookingToPurchase'
        label={t('groupForm.seats.form.whenAreYouLookingToPurchase.label')}
        options={[
          t('groupForm.seats.form.whenAreYouLookingToPurchase.options.30'),
          t('groupForm.seats.form.whenAreYouLookingToPurchase.options.60'),
          t('groupForm.seats.form.whenAreYouLookingToPurchase.options.90'),
          t('groupForm.seats.form.whenAreYouLookingToPurchase.options.other'),
        ].map((value) => ({
          value,
          label: value,
        }))}
        placeholder={t(
          'groupForm.seats.form.whenAreYouLookingToPurchase.placeholder',
        )}
        onChange={(value: number) =>
          handleValueChange(value, form, 'whenAreYouLookingToPurchase')
        }
        required
      />
      <Field
        component={TextareaField}
        id='purpose'
        name='purpose'
        label={t('groupForm.seats.form.purpose.label')}
        type='text'
        placeholder={t('groupForm.seats.form.purpose.label')}
        maxlength={256}
      />

      <p className='mc-opacity--muted mc-mt-4'>
        <Trans
          i18nKey='groupForm.seats.privacyTerms'
          ns='@mc/enterprise-contact-form'
        >
          <a href='/privacy' target='_blank' className='mc-text--link'>
            {t('groupForm.v2.privacyPolicy')}
          </a>
        </Trans>
      </p>

      <Field
        component={CheckboxField}
        name='gdprOptIn'
        label={t('groupForm.seats.gdprLabel')}
        required
      />

      <Button
        disabled={submitted || !valid}
        type='submit'
        className='mc-m-auto mc-mt-4'
        loading={loading}
      >
        {t('groupForm.v2.contactUs')}
      </Button>

      {errors && (
        <p className='mc-text--error mc-mt-4' role='alert'>
          {t('groupForm.seats.submit.fail')}
        </p>
      )}
    </form>
  )
}
